import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import leaveService, {
  type LeaveType,
  type UpdateLeaveRequestData,
} from "../services/leave.service";
import { ceToBe } from "../utils/dateUtils";
import { useAuth } from "../hooks/useAuth";
import { DatePicker } from "../components/ui/date-picker";
import { MainLayout } from "../components/layout";
import { useNavigate } from "../lib/router";

export const EditLeaveRequestPage: React.FC = () => {
  // Get ID from URL
  const id = window.location.pathname.split("/").pop();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  // Check if user is admin or HR
  const isAdminOrHR =
    currentUser?.role === "admin" || currentUser?.role === "hr";

  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    isHalfDayStart: false,
    isHalfDayEnd: false,
    reason: "",
    contactDuringLeave: "",
    emergencyContact: "",
  });

  const [error, setError] = useState("");

  // Fetch leave request
  const {
    data: leaveRequest,
    isLoading: loadingRequest,
    error: requestError,
  } = useQuery({
    queryKey: ["leaveRequest", id],
    queryFn: () => leaveService.getLeaveRequest(id!),
    enabled: !!id,
  });

  // Fetch leave types
  const { data: leaveTypesData, isLoading: loadingTypes } = useQuery({
    queryKey: ["leaveTypes"],
    queryFn: () => leaveService.getLeaveTypes(),
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: UpdateLeaveRequestData) =>
      leaveService.updateLeaveRequest(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaveRequests"] });
      queryClient.invalidateQueries({ queryKey: ["leaveRequest", id] });
      navigate("/leave");
    },
    onError: (error) => {
      console.error("Error updating request:", error);
      setError(
        error instanceof Error ? error.message : "Failed to update request"
      );
    },
  });

  useEffect(() => {
    if (leaveRequest) {
      setFormData({
        startDate: leaveRequest.startDate,
        endDate: leaveRequest.endDate,
        isHalfDayStart: leaveRequest.isHalfDayStart || false,
        isHalfDayEnd: leaveRequest.isHalfDayEnd || false,
        reason: leaveRequest.reason,
        contactDuringLeave: leaveRequest.contactDuringLeave || "",
        emergencyContact: leaveRequest.emergencyContact || "",
      });
    }
  }, [leaveRequest]);

  const selectedLeaveType = leaveTypesData?.leaveTypes.find(
    (type: LeaveType) => type.id === leaveRequest?.leaveTypeId
  ) || leaveRequest?.leaveType;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validate form
    if (!formData.startDate || !formData.endDate || !formData.reason.trim()) {
      setError("Please fill in all required fields");
      return;
    }

    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      setError("Start date must be before end date");
      return;
    }

    // Only send changed fields
    const updateData: UpdateLeaveRequestData = {};

    if (leaveRequest) {
      if (formData.startDate !== leaveRequest.startDate) {
        updateData.startDate = formData.startDate;
      }
      if (formData.endDate !== leaveRequest.endDate) {
        updateData.endDate = formData.endDate;
      }
      if (formData.isHalfDayStart !== leaveRequest.isHalfDayStart) {
        updateData.isHalfDayStart = formData.isHalfDayStart;
      }
      if (formData.isHalfDayEnd !== leaveRequest.isHalfDayEnd) {
        updateData.isHalfDayEnd = formData.isHalfDayEnd;
      }
      if (formData.reason !== leaveRequest.reason) {
        updateData.reason = formData.reason;
      }
      if (
        formData.contactDuringLeave !== (leaveRequest.contactDuringLeave || "")
      ) {
        updateData.contactDuringLeave = formData.contactDuringLeave;
      }
      if (formData.emergencyContact !== (leaveRequest.emergencyContact || "")) {
        updateData.emergencyContact = formData.emergencyContact;
      }
    }

    updateMutation.mutate(updateData);
  };

  const calculateBusinessDays = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    let businessDays = 0;
    const current = new Date(startDate);

    while (current <= endDate) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
      current.setDate(current.getDate() + 1);
    }

    if (formData.isHalfDayStart) businessDays -= 0.5;
    if (formData.isHalfDayEnd) businessDays -= 0.5;

    return Math.max(0, businessDays);
  };

  const totalDays =
    formData.startDate && formData.endDate
      ? calculateBusinessDays(formData.startDate, formData.endDate)
      : 0;

  // Loading states
  if (loadingRequest || loadingTypes) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error states
  if (requestError || !leaveRequest) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <p className="font-medium">Error loading leave request</p>
            <p className="text-sm">
              {requestError instanceof Error
                ? requestError.message
                : "Leave request not found"}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Check if request can be edited
  if (leaveRequest.status !== "pending") {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg">
            <p className="font-medium">ไม่สามารถแก้ไขคำขอนี้ได้</p>
            <p className="text-sm">
              สถานะปัจจุบันของคำขอนี้คือ {leaveRequest.status}.
            </p>
            <button
              onClick={() => navigate("/leave")}
              className="mt-3 text-blue-600 hover:text-blue-800 underline"
            >
              กลับไปหน้าคำขอลา
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MainLayout title="แก้ไขคำขอลา">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            อัปเดตคำขอลา {selectedLeaveType?.name} ของคุณ
          </p>
        </div>

        {/* Form */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg">
                <p className="font-medium">Error</p>
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Leave Type (Read-only) */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                ประเภทการลา
              </label>
              <div className="flex items-center space-x-2 p-3 bg-gray-50 dark:bg-gray-700 border dark:border-gray-600 rounded-md">
                <span className="text-lg">{selectedLeaveType?.icon}</span>
                <span className="font-medium text-gray-900 dark:text-gray-100">{selectedLeaveType?.name}</span>
              </div>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="startDate"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  วันเริ่มต้น*
                  {isAdminOrHR && (
                    <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                      (สามารถเลือกวันที่ย้อนหลังได้)
                    </span>
                  )}
                </label>
                <DatePicker
                  value={formData.startDate}
                  onChange={(ceDate) =>
                    setFormData({ ...formData, startDate: ceDate || "" })
                  }
                  minDate={!isAdminOrHR ? new Date() : undefined}
                  placeholder="เลือกวันที่"
                  className="w-full"
                />
                {formData.startDate && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    พ.ศ. {ceToBe(new Date(formData.startDate).getFullYear())}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="endDate"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  วันสิ้นสุด*
                </label>
                <DatePicker
                  value={formData.endDate}
                  onChange={(ceDate) =>
                    setFormData({ ...formData, endDate: ceDate || "" })
                  }
                  minDate={
                    formData.startDate
                      ? new Date(formData.startDate)
                      : !isAdminOrHR
                        ? new Date()
                        : undefined
                  }
                  placeholder="เลือกวันที่"
                  className="w-full"
                />
                {formData.endDate && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    พ.ศ. {ceToBe(new Date(formData.endDate).getFullYear())}
                  </p>
                )}
              </div>
            </div>

            {/* Backdated Leave Warning */}
            {isAdminOrHR &&
              formData.startDate &&
              new Date(formData.startDate) <
                new Date(new Date().setHours(0, 0, 0, 0)) && (
                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-200 rounded-lg">
                  <p className="text-sm flex items-center">
                    <span className="text-lg mr-2">⚠️</span>
                    <span>
                      <strong>คำเตือน:</strong>{" "}
                      คุณกำลังแก้ไขคำขอลาให้เป็นวันที่ย้อนหลัง
                    </span>
                  </p>
                </div>
              )}

            {/* Half Day Options */}
            {selectedLeaveType?.allowHalfDay && (
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <input
                    id="halfDayStart"
                    type="checkbox"
                    checked={formData.isHalfDayStart}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isHalfDayStart: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="halfDayStart"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    ลาครึ่งวันแรก
                  </label>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="halfDayEnd"
                    type="checkbox"
                    checked={formData.isHalfDayEnd}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        isHalfDayEnd: e.target.checked,
                      })
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="halfDayEnd"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    ลาครึ่งวันสุดท้าย
                  </label>
                </div>
              </div>
            )}

            {/* Duration Display */}
            {totalDays > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-md">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Duration:</strong> {totalDays} business day
                  {totalDays !== 1 ? "s" : ""}
                </p>
              </div>
            )}

            {/* Reason */}
            <div className="space-y-2">
              <label
                htmlFor="reason"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                เหตุผล*
              </label>
              <textarea
                id="reason"
                value={formData.reason}
                onChange={(e) =>
                  setFormData({ ...formData, reason: e.target.value })
                }
                placeholder="Please provide a reason for this leave request..."
                rows={3}
                required
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="contactDuringLeave"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  ข้อมูลติดต่อช่วงการลา
                </label>
                <input
                  id="contactDuringLeave"
                  type="tel"
                  value={formData.contactDuringLeave}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactDuringLeave: e.target.value,
                    })
                  }
                  placeholder="Phone number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="emergencyContact"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  เบอร์โทรศัพท์ฉุกเฉิน
                </label>
                <input
                  id="emergencyContact"
                  type="tel"
                  value={formData.emergencyContact}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emergencyContact: e.target.value,
                    })
                  }
                  placeholder="Emergency phone number"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate("/leave")}
                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                ยกเลิก
              </button>
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {updateMutation.isPending ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    กำลังอัปเดต...
                  </>
                ) : (
                  "Update Request"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditLeaveRequestPage;
